import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { revisarArchivo, medidasDestino, pesoDe, esImagenValida,
         LADO_MAXIMO, MAXIMO_BYTES } from '../utils/logo';

// Los datos de la empresa: los que van en el encabezado de los papeles.
//
// El NIT y la dirección no son adorno. Un papel que solo dice el nombre no se
// ve salido de una empresa, y el finquero o el mayorista que lo recibe lo
// guarda como comprobante.

function Ajustes() {
  const [a, setA] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const archivo = useRef(null);

  const cargar = useCallback(() => {
    axios.get('/api/ajustes')
      .then(r => setA({ nombre_negocio: '', nit: '', direccion: '', ciudad: '',
                        telefono: '', whatsapp_parte: '', logo: null, ...(r.data || {}) }))
      .catch(() => setAviso({ tipo: 'malo', texto: 'No se pudieron cargar los ajustes' }));
  }, []);

  useEffect(cargar, [cargar]);

  // Achicar antes de guardar. La foto que sale de un teléfono pesa tres megas y
  // eso no cabe en un renglón que se lee en cada pantalla.
  const escoger = (e) => {
    const f = e.target.files && e.target.files[0];
    const problema = revisarArchivo(f);
    if (problema) { setAviso({ tipo: 'malo', texto: problema }); return; }

    const lector = new FileReader();
    lector.onload = () => {
      const img = new Image();
      img.onload = () => {
        const m = medidasDestino(img.width, img.height, LADO_MAXIMO);
        if (!m) { setAviso({ tipo: 'malo', texto: 'No se pudo leer esa imagen.' }); return; }
        const lienzo = document.createElement('canvas');
        lienzo.width = m.ancho; lienzo.height = m.alto;
        const ctx = lienzo.getContext('2d');
        // Fondo blanco: un PNG con transparencia sobre papel blanco se ve
        // igual, pero convertido a JPEG sin fondo la transparencia sale negra.
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, m.ancho, m.alto);
        ctx.drawImage(img, 0, 0, m.ancho, m.alto);
        // PNG para no perder los bordes limpios de un logo. Si se pasa del
        // tope, se reintenta en JPEG, que pesa mucho menos.
        let salida = lienzo.toDataURL('image/png');
        if (pesoDe(salida) > MAXIMO_BYTES) salida = lienzo.toDataURL('image/jpeg', 0.85);
        if (pesoDe(salida) > MAXIMO_BYTES) {
          setAviso({ tipo: 'malo', texto: 'No se pudo achicar lo suficiente. ' +
                                          'Pruebe con una imagen más sencilla.' });
          return;
        }
        setA(x => ({ ...x, logo: salida }));
        setAviso({ tipo: 'bueno',
          texto: `Logo listo (${Math.round(pesoDe(salida) / 1024)} KB). ` +
                 'Falta guardar abajo.' });
      };
      img.onerror = () => setAviso({ tipo: 'malo', texto: 'Ese archivo no es una imagen.' });
      img.src = lector.result;
    };
    lector.onerror = () => setAviso({ tipo: 'malo', texto: 'No se pudo leer el archivo.' });
    lector.readAsDataURL(f);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.put('/api/ajustes', a);
      setAviso({ tipo: 'bueno', texto: 'Guardado. Ya sale en los papeles y en el menú.' });
      // El menú lee el logo de la sesión guardada: sin esto habría que salir y
      // volver a entrar para verlo.
      try {
        const s = JSON.parse(localStorage.getItem('quesera-sesion'));
        if (s && s.user) {
          s.user.logo = a.logo; s.user.nit = a.nit;
          s.user.direccion = a.direccion; s.user.ciudad = a.ciudad;
          s.user.telefono = a.telefono;
          if (a.nombre_negocio) s.user.negocio = a.nombre_negocio;
          localStorage.setItem('quesera-sesion', JSON.stringify(s));
        }
      } catch { /* si no se puede, el logo sale igual al recargar */ }
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  if (!a) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  return (
    <>
      <div className="encabezado">
        <h1>Ajustes</h1>
        <p>Los datos de su empresa, los que salen impresos en cada papel</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <form onSubmit={guardar}>
        <div className="bloque">
          <h2>🏢 La empresa</h2>
          <div className="cuerpo">
            <div className="campos">
              <label className="campo">
                <span>Nombre o razón social *</span>
                <input value={a.nombre_negocio || ''} required
                       onChange={e => setA({ ...a, nombre_negocio: e.target.value })} />
              </label>
              <label className="campo">
                <span>NIT</span>
                <input value={a.nit || ''} placeholder="1083829770-3"
                       onChange={e => setA({ ...a, nit: e.target.value })} />
              </label>
              <label className="campo">
                <span>Dirección</span>
                <input value={a.direccion || ''} placeholder="calle 2 B 6-37"
                       onChange={e => setA({ ...a, direccion: e.target.value })} />
              </label>
              <label className="campo">
                <span>Ciudad</span>
                <input value={a.ciudad || ''} placeholder="Fundación, Magdalena"
                       onChange={e => setA({ ...a, ciudad: e.target.value })} />
              </label>
              <label className="campo">
                <span>Teléfono</span>
                <input value={a.telefono || ''}
                       onChange={e => setA({ ...a, telefono: e.target.value })} />
              </label>
            </div>
            <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', margin: '10px 0 0' }}>
              Esto va en el encabezado de la <strong>relación</strong>, el
              <strong> comprobante de egreso</strong> y la <strong>remisión</strong>.
              Un papel que solo dice el nombre no se ve salido de una empresa.
            </p>
          </div>
        </div>

        {/* A QUIÉN SE LE MANDA EL PARTE.
            
            Aparte del teléfono del negocio a propósito. Ése sale impreso en las
            remisiones y los comprobantes: poniendo aquí el celular del patrón
            se lo pondríamos a todos los papeles que salen de la quesera, y a
            nadie se le ocurre buscar la causa en Ajustes cuando lo ve en una
            factura del mes pasado. */}
        <div className="bloque">
          <h2>📲 El parte diario</h2>
          <div className="cuerpo">
            <div className="campos">
              <label className="campo">
                <span>Celular al que se manda</span>
                <input value={a.whatsapp_parte || ''}
                       placeholder="300 123 4567"
                       onChange={e => setA({ ...a, whatsapp_parte: e.target.value })} />
              </label>
            </div>
            <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', margin: '10px 0 0' }}>
              El celular del dueño, para el botón de <strong>Rutas</strong> que manda
              los promedios del día. Al tocarlo se abre WhatsApp con el parte ya
              escrito, en su conversación — y quien lo manda le da a enviar.
              {' '}<strong>Nunca sale solo.</strong>
            </p>
            <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', margin: '6px 0 0' }}>
              Si se deja vacío, el botón igual sirve: abre WhatsApp con el parte
              escrito y hay que elegir a quién. Diez dígitos son de Colombia; para
              otro país hay que poner el indicativo delante.
            </p>
          </div>
        </div>

        <div className="bloque">
          <h2>🖼️ El logo</h2>
          <div className="cuerpo">
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start',
                          flexWrap: 'wrap' }}>
              <div style={{
                width: 130, height: 130, flexShrink: 0, borderRadius: 10,
                border: '1px dashed var(--linea)', background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {esImagenValida(a.logo)
                  ? <img src={a.logo} alt="Logo"
                         style={{ maxWidth: '100%', maxHeight: '100%' }} />
                  : <span style={{ color: 'var(--tinta-tenue)', fontSize: 12 }}>
                      sin logo
                    </span>}
              </div>

              <div style={{ flex: '1 1 260px' }}>
                <input type="file" ref={archivo} accept="image/png,image/jpeg,image/webp"
                       onChange={escoger} style={{ display: 'none' }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="boton suave"
                          onClick={() => archivo.current && archivo.current.click()}>
                    📁 Escoger imagen
                  </button>
                  {a.logo && (
                    <button type="button" className="boton suave"
                            onClick={() => { setA({ ...a, logo: null });
                                             setAviso({ tipo: 'ojo',
                                               texto: 'Logo quitado. Falta guardar abajo.' }); }}>
                      Quitar
                    </button>
                  )}
                </div>
                <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 10 }}>
                  PNG, JPG o WEBP. Puede mandar la foto tal cual del teléfono: el
                  programa la achica a {LADO_MAXIMO} píxeles antes de guardarla.
                </p>
                <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', margin: 0 }}>
                  Sale en los <strong>tres papeles</strong> y en el menú, arriba.
                  No sale en la pantalla de entrar: ahí todavía no se sabe de qué
                  empresa es quien está llegando.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bloque">
          <div className="acciones" style={{ padding: 16 }}>
            <button className="boton" disabled={guardando}>
              {guardando ? 'Guardando…' : '💾 Guardar'}
            </button>
          </div>
        </div>
      </form>
    </>
  );
}

export default Ajustes;
